"use server";

import { auth } from "@/auth";
import { prisma } from "@/prisma";
import { validateParams } from "@/lib/utils";
import z from "zod";
import type { ActionResult } from "@/types/server-action";
import { canAccessDraft } from "@/lib/cat/draft-access";
import {
  acquireDraftLock,
  buildDraftLockHolder,
  getDraftLockHolder,
  releaseDraftLock,
} from "@/lib/cat/draft-lock";
import { resolveVehicleIdentificationSearch } from "@/lib/cat/vehicle-identification-resolve";
import type {
  DraftResumeInconsistency,
  DraftResumeResult,
} from "@/types/cat/drafts";
import type { SearchVehicleDataResult } from "@/lib/cat/mulesoft-vehicle-search";

const resumeDraftSchema = z.object({
  draftId: z.number().int().positive(),
});

type ResumeDraftActionResult = ActionResult<DraftResumeResult> & {
  code?: "LOCKED";
  editorName?: string;
};

export async function resumeDraftAction(
  params: z.infer<typeof resumeDraftSchema>,
): Promise<ResumeDraftActionResult> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }

    const { draftId } = validateParams(resumeDraftSchema, params);
    const draft = await prisma.draft.findUnique({
      where: { id: draftId },
    });
    if (!draft) {
      return { success: false, message: "Brouillon introuvable." };
    }

    if (!canAccessDraft(session, draft)) {
      return { success: false, message: "Accès refusé à ce brouillon." };
    }

    const lockHolder = buildDraftLockHolder(session.user);
    const lockAcquired = await acquireDraftLock(draftId, lockHolder);

    if (!lockAcquired) {
      const holder = await getDraftLockHolder(draftId);
      return {
        success: false,
        code: "LOCKED",
        editorName: holder?.userName,
        message: holder
          ? `Brouillon en cours d'édition par ${holder.userName}.`
          : "Brouillon en cours d'édition par un autre utilisateur.",
      };
    }

    const brand = session.data?.kvps?.current?.parts?.brand?.trim();
    if (!brand) {
      await releaseDraftLock(draftId, session.user.id);
      return { success: false, message: "KVPS session invalide." };
    }

    if (!draft.commissionYear || !draft.commissionNumber) {
      await releaseDraftLock(draftId, session.user.id);
      return {
        success: false,
        message: "Le brouillon ne contient pas d'identifiant commande valide.",
      };
    }

    const inconsistencies: DraftResumeInconsistency[] = [];
    const outcome = await resolveVehicleIdentificationSearch({
      brand,
      commissionNumber: draft.commissionNumber,
      commissionYear: draft.commissionYear,
      vin: draft.vinNumber ?? undefined,
      bid: "995",
    });

    if (outcome.kind === "error") {
      await releaseDraftLock(draftId, session.user.id);
      return { success: false, message: outcome.message };
    }

    if (outcome.kind !== "data") {
      await releaseDraftLock(draftId, session.user.id);
      return {
        success: false,
        message: "Impossible de retrouver le véhicule dans MuleSoft.",
      };
    }

    const muleData = outcome.data;

    if (muleData.isAlreadyAssigned) {
      await releaseDraftLock(draftId, session.user.id);
      return {
        success: false,
        message: "Le véhicule est déjà contremarqué.",
      };
    }

    const sessionIdentifier = session.data?.kvps?.current?.identifier;
    const isHeadOffice =
      session.data?.kvps?.current?.parts?.wholesalerNumber === "995";

    if (
      !isHeadOffice &&
      session.data?.kvps?.current?.parts?.country === "FRA" &&
      muleData.owningDealerKvps !== sessionIdentifier
    ) {
      await releaseDraftLock(draftId, session.user.id);
      return {
        success: false,
        message: "Le KVPS propriétaire du véhicule a changé.",
      };
    }

    if (
      muleData.commissionYear &&
      muleData.commissionYear !== draft.commissionYear
    ) {
      inconsistencies.push({
        stepId: "vehicle_identification",
        message:
          "L'année de commande MuleSoft ne correspond plus au brouillon.",
        blocking: true,
      });
    }

    if (
      muleData.commissionNumber &&
      muleData.commissionNumber !== draft.commissionNumber
    ) {
      inconsistencies.push({
        stepId: "vehicle_identification",
        message:
          "Le numéro de commande MuleSoft ne correspond plus au brouillon.",
        blocking: true,
      });
    }

    const muleVin = muleData.vin?.trim();
    if (draft.vinNumber && muleVin && draft.vinNumber !== muleVin) {
      inconsistencies.push({
        stepId: "vehicle_identification",
        message: "Le VIN MuleSoft ne correspond plus au brouillon.",
        blocking: true,
      });
    }

    const blocking = inconsistencies.some((item) => item.blocking);
    if (blocking) {
      await releaseDraftLock(draftId, session.user.id);
      return {
        success: false,
        message:
          inconsistencies[0]?.message ?? "Incohérence véhicule détectée.",
      };
    }

    const muleModel = muleData.modelDescription?.trim();
    const draftModel = draft.vehicleModelDescription?.trim();
    if (muleModel && !draftModel) {
      inconsistencies.push({
        stepId: "vehicle_identification",
        message: "Modèle véhicule désormais disponible dans MuleSoft.",
        blocking: false,
      });
    } else if (draftModel && muleModel && draftModel !== muleModel) {
      inconsistencies.push({
        stepId: "vehicle_identification",
        message: "Le modèle MuleSoft a évolué depuis la dernière sauvegarde.",
        blocking: false,
      });
    }

    const updateData: {
      lockedByUserId: string;
      vehicleModelDescription?: string;
      vinNumber?: string;
    } = {
      lockedByUserId: lockHolder.userId,
    };

    if (muleModel && muleModel !== draftModel) {
      updateData.vehicleModelDescription = muleModel;
    }
    if (muleVin && !draft.vinNumber) {
      updateData.vinNumber = muleVin;
    }

    const updatedDraft = await prisma.draft.update({
      where: { id: draftId },
      data: updateData,
    });

    const muleSoftVehicleData: SearchVehicleDataResult = {
      ...muleData,
      owningDealerKvps: muleData.owningDealerKvps || draft.kvps,
    };

    return {
      success: true,
      data: {
        draft: updatedDraft,
        muleSoftVehicleData,
        stepIndex: Math.min(Math.max(updatedDraft.currentStepIndex, 1), 8),
        inconsistencies,
      },
    };
  } catch (error) {
    console.error("[resumeDraftAction]", error);
    return { success: false, message: "Une erreur serveur est survenue." };
  }
}
